import { z } from 'zod';

import { addressSchema } from '../web3';

/**
 * Generic token schema for tokens that don't fit into EVM or OTC categories
 * (e.g., Coinbase donations). Uses `z.string()` for type to allow flexibility
 * in backend type values, but this prevents TypeScript from using discriminated
 * union narrowing based on the `type` property.
 *
 * When narrowing token types in code, use property-based type guards:
 * - Both OTCToken and EVMToken can have 'otcAddress' - use it to get the send address
 * - Use `'otcNetwork' in token` or `'memo' in token` to check for OTCToken-specific features
 * - Use `'chainId' in token` or `token.type === 'EvmToken'` to check for EVMToken
 */
export const genericTokenSchema = z.object({
  id: z.number(),
  symbol: z.string(),
  name: z.string(),
  decimals: z.number(),
  logoUrl: z.string(),
  type: z.string(),
  featured: z.boolean().default(false),
  popularity: z.number().default(0),
});

export const evmTokenSchema = genericTokenSchema.extend({
  type: z.literal('EvmToken'),
  chainId: z.number(),
  contractAddress: addressSchema,
  // EVM tokens may have an OTC address for direct transfers (generated from Coinbase Auto-Liquidator)
  otcAddress: addressSchema.optional(),
});

export const otcTokenSchema = genericTokenSchema.extend({
  type: z.literal('OtcToken'),
  otcAddress: z.string(),
  memo: z.string().nullish(),
  otcNetwork: z.object({ id: z.string(), type: z.string() }).optional(),
});

export type GenericToken = z.infer<typeof genericTokenSchema>;
export type EVMToken = z.infer<typeof evmTokenSchema>;
export type OTCToken = z.infer<typeof otcTokenSchema>;

export type TokenBalance<T extends Pick<EVMToken, 'contractAddress' | 'decimals' | 'symbol'> = EVMToken> = {
  token: T;
  balance: bigint;
};
